/* eslint-disable perfectionist/sort-objects */ // for breakpoints
/* eslint-disable perfectionist/sort-imports */
import { FC, ReactNode, useEffect, useMemo } from 'react'

import { MantineProvider, MantineThemeOverride } from '@mantine/core'

import '@mantine/core/styles.css'
import s from './s.module.scss'
import { IconCheck } from '@tabler/icons-react'
import { COLOR_SCHEME, variantsTheme } from './types'

type Props = {
  children: ReactNode
}

export const THEME_BG = {
  'dark-theme': '#151e25',
  'light-theme': '#ebf0f7',
} as const

const Themes: FC<Props> = ({ children }) => {
  const savedTheme = localStorage.getItem(COLOR_SCHEME)
  const showSwitcher = ['localhost', 'dev', 'demo'].some((key) => window.location.origin.includes(key))

  const isDarkTheme = showSwitcher
    ? savedTheme === variantsTheme.DARK
    : String(import.meta.env.VITE_THEME_IS_DARK) === 'true'

  useEffect(() => {
    if (isDarkTheme) {
      localStorage.setItem(COLOR_SCHEME, variantsTheme.DARK)
      document.body.classList.remove(variantsTheme.LIGHT, variantsTheme.DARK)
      document.body.classList.add(variantsTheme.DARK)
      document.documentElement.style.backgroundColor = THEME_BG['dark-theme']
      document.body.style.background = THEME_BG['dark-theme']
    } else {
      localStorage.setItem(COLOR_SCHEME, variantsTheme.LIGHT)
      document.body.classList.remove(variantsTheme.LIGHT, variantsTheme.DARK)
      document.body.classList.add(variantsTheme.LIGHT)
      document.documentElement.style.backgroundColor = THEME_BG['light-theme']
      document.body.style.background = THEME_BG['light-theme']
    }
  }, [])

  const defaultColorSchemeMantine = isDarkTheme ? 'dark' : 'light'

  const theme = useMemo<MantineThemeOverride>(
    () => ({
      breakpoints: {
        xs: '30em', // 480px
        sm: '48em', // 768px
        md: '64em', // 1024px
        lg: '74em', // 1184px
        xl: '90em', // 1440px
      },
      components: {
        Button: {
          defaultProps: {
            classNames: { inner: s.buttonInner, root: s.buttonRoot },
            size: 'md',
          },
        },
        Checkbox: {
          defaultProps: {
            classNames: {
              body: s.checkboxBody,
              icon: s.checkboxIcon,
              input: s.checkboxInput,
              label: s.checkboxLabel,
              root: s.checkboxRoot,
            },
            icon: IconCheck,
          },
        },
        Dropzone: {
          defaultProps: {
            classNames: { root: s.dropzoneRoot },
          },
        },
        Input: {
          defaultProps: {
            classNames: { input: s.inputCustom, wrapper: s.inputWrapper },
            size: 'xl',
          },
        },
        NumberInput: {
          defaultProps: {
            classNames: { error: s.inputError, input: s.inputCustom, wrapper: s.inputWrapper },
            size: 'xl',
          },
        },
        Pagination: {
          defaultProps: {
            classNames: {
              root: s.paginationRoot,
              control: s.paginationControl,
              dots: s.paginationDots,
            },
          },
        },
        Paper: {
          defaultProps: {
            classNames: { root: s.paperRoot },
          },
        },
        SegmentedControl: {
          defaultProps: {
            classNames: { indicator: s.segmentedIndicator, label: s.segmentedLabel, root: s.segmentedRoot },
          },
        },
        Select: {
          defaultProps: {
            classNames: {
              dropdown: s.selectDropDown,
              input: s.selectInput,
              option: s.selectOption,
              wrapper: s.selectWrapper,
            },
            size: 'xl',
          },
        },
        Skeleton: {
          defaultProps: {
            classNames: { root: s.skeletonRoot },
          },
        },
        Switch: {
          defaultProps: {
            classNames: {
              input: s.switchInput,
              thumb: s.thumb,
              track: s.track,
            },
            size: 'lg',
          },
        },
        Table: {
          defaultProps: {
            classNames: { table: s.tableStriped, td: s.tableTd, th: s.tableTh, tr: s.tableTr },
          },
        },
        Tabs: {
          defaultProps: {
            classNames: { list: s.tabsList, tab: s.tabsTab },
          },
        },
        Textarea: { classNames: { label: s.textAreaLabel } },
        TextInput: {
          defaultProps: {
            classNames: { input: s.inputCustom, wrapper: s.inputWrapper },
            size: 'xl',
          },
        },
        Menu: {
          defaultProps: {
            classNames: {
              dropdown: s.menuDropdown,
              item: s.menuItem,
            },
          },
        },
        Divider: {
          defaultProps: {
            classNames: { root: s.divider },
          },
        },
        Tooltip: {
          defaultProps: {
            classNames: { tooltip: s.tooltip },
          },
        },
        HoverCard: {
          defaultProps: {
            classNames: { dropdown: s.hoverCard },
          },
        },
        Modal: {
          classNames: {
            content: s.modal,
          },
        },
        DatePickerInput: {
          defaultProps: {
            size: 'xl',
            classNames: {
              input: s.background,
            },
          },
        },
        Popover: {
          defaultProps: {
            classNames: {
              dropdown: s.backgroundSecond,
            },
          },
        },
      },
      fontFamily: 'Inter, sans-serif',
    }),
    [],
  )

  return (
    <MantineProvider defaultColorScheme={defaultColorSchemeMantine} theme={theme}>
      {children}
    </MantineProvider>
  )
}
export default Themes
